/**
 * iter233 — Références / en-têtes du dossier OR.
 *
 * Aucun champ affiché par défaut : un bouton discret « + Ajouter un en-tête /
 * une référence » propose les types prédéfinis ou une référence libre (nom +
 * valeur). Chaque référence ajoutée s'affiche en petit champ avec un × pour la
 * retirer. Les valeurs sont enregistrées immédiatement (PATCH /or/{id}) et
 * suivent le dossier : OR → devis → facture → e-mails → recherche globale.
 */
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, X, Tag } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { REFERENCE_TYPES, FREE_KEY } from "@/lib/orReferences";

/**
 * iter241 — Mode local : passer `value` + `onChange` (sans `or.id`) permet de
 * réutiliser le bloc dans la saisie d'un devis ou d'une facture, avant
 * enregistrement. Sinon, chaque modification est persistée sur l'OR.
 */
export default function OrReferencesBlock({ or, onUpdated, value, onChange }) {
  const local = typeof onChange === "function" && !or?.id;
  const [refs, setRefs] = useState(value || or?.references || []);
  const [free, setFree] = useState({ open: false, label: "", value: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!local) setRefs(or?.references || []); }, [local, or?.id, or?.references]);

  const persist = async (next) => {
    if (local) { setRefs(next); onChange(next); return true; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/or/${or.id}`, { references: next });
      setRefs(data.references || []);
      onUpdated?.(data);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement de la référence");
      setRefs(or?.references || []);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const addType = (type) => {
    if (refs.some((r) => r.key === type.key)) return;
    persist([...refs, { key: type.key, label: type.label, value: "" }]);
  };

  const addFree = () => {
    const label = free.label.trim();
    if (!label) { toast.error("Donne un nom à la référence"); return; }
    persist([...refs, { key: FREE_KEY, label, value: free.value.trim() }]);
    setFree({ open: false, label: "", value: "" });
  };

  const remove = (idx) => persist(refs.filter((_, i) => i !== idx));

  const setValue = (idx, value) => {
    setRefs((prev) => prev.map((r, i) => (i === idx ? { ...r, value } : r)));
  };

  const commitValue = (idx) => {
    const serverVal = (or?.references || [])[idx]?.value || "";
    if ((refs[idx]?.value || "") === serverVal) return;
    persist(refs);
  };

  const available = REFERENCE_TYPES.filter((t) => !refs.some((r) => r.key === t.key));

  return (
    <div data-testid="or-references-block" className="mt-3">
      {refs.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {refs.map((r, idx) => (
            <div
              key={`${r.key}-${r.label}-${idx}`}
              className="group flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-full border border-gray-200 bg-white hover:border-[#52a675]/50 transition-colors"
              data-testid={`or-ref-chip-${r.key}`}
            >
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 whitespace-nowrap">
                {r.label}
              </span>
              <Input
                value={r.value || ""}
                onChange={(e) => setValue(idx, e.target.value)}
                onBlur={() => commitValue(idx)}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                placeholder="—"
                className="h-6 w-32 px-1.5 py-0 text-sm border-0 shadow-none focus-visible:ring-0 bg-transparent font-mono"
                data-testid={`or-ref-input-${r.key}`}
              />
              <button
                type="button"
                onClick={() => remove(idx)}
                disabled={busy}
                title="Retirer cette référence"
                className="w-5 h-5 rounded-full text-gray-300 hover:text-red-600 hover:bg-red-50 flex items-center justify-center"
                data-testid={`or-ref-remove-${r.key}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="text-xs text-gray-500 hover:text-[#3d8a5c] inline-flex items-center gap-1 hover:underline"
            data-testid="or-ref-add-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Ajouter un en-tête / une référence
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-gray-400">
            Références prédéfinies
          </DropdownMenuLabel>
          {available.map((t) => (
            <DropdownMenuItem
              key={t.key}
              onClick={() => addType(t)}
              data-testid={`or-ref-type-${t.key}`}
            >
              <Tag className="w-3.5 h-3.5 mr-2 text-gray-400" /> {t.label}
            </DropdownMenuItem>
          ))}
          {available.length === 0 && (
            <div className="px-2 py-1.5 text-xs text-gray-400">Toutes déjà ajoutées</div>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => setFree({ open: true, label: "", value: "" })}
            data-testid="or-ref-free-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-2 text-[#52a675]" /> Référence libre…
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={free.open} onOpenChange={(o) => !o && setFree({ open: false, label: "", value: "" })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Référence libre</DialogTitle>
            <DialogDescription>
              Ex. « N° commande » / « 45872 ». Elle apparaît sur le devis et la facture.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nom de la référence</Label>
              <Input
                value={free.label}
                onChange={(e) => setFree((p) => ({ ...p, label: e.target.value }))}
                placeholder="N° commande"
                autoFocus
                data-testid="or-ref-free-label"
              />
            </div>
            <div>
              <Label className="text-xs">Valeur</Label>
              <Input
                value={free.value}
                onChange={(e) => setFree((p) => ({ ...p, value: e.target.value }))}
                placeholder="45872"
                onKeyDown={(e) => { if (e.key === "Enter") addFree(); }}
                data-testid="or-ref-free-value"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFree({ open: false, label: "", value: "" })}>
              Annuler
            </Button>
            <Button
              onClick={addFree}
              disabled={busy}
              className="bg-[#52a675] hover:bg-[#3d8a5c]"
              data-testid="or-ref-free-submit"
            >
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
